import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Truck, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart, useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart, token } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [paymentMode, setPaymentMode] = useState("deposit");

  const [form, setForm] = useState({
    shipping_address: "",
    shipping_city: "",
    shipping_postal: "",
    shipping_country: "France",
    notes: ""
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(price);
  };

  const isDeposit = paymentMode === "deposit";
  const totalHT = cart.total;
  const tva = totalHT * 0.20;
  const totalTTC = totalHT + tva;
  const payableNow = isDeposit ? totalTTC * 0.5 : totalTTC;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1. Create order in our system
      const orderRes = await axios.post(`${API}/orders`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newOrderId = orderRes.data.id;
      setOrderId(newOrderId);

      // 2. Create SumUp checkout
      const checkoutRes = await axios.post(`${API}/checkout/create`, {
        order_id: newOrderId,
        amount: totalTTC,
        description: `Commande MOREATI #${newOrderId.slice(0, 8)}`,
        customer_email: user?.email || "",
        is_deposit: isDeposit,
        redirect_url: `${window.location.origin}/checkout?order=${newOrderId}&status=complete`,
      });

      // 3. Redirect to SumUp payment page
      if (checkoutRes.data.payment_url) {
        clearCart();
        window.location.href = checkoutRes.data.payment_url;
      } else {
        setOrderComplete(true);
        clearCart();
        toast.success("Commande passée avec succès !");
      }
    } catch (e) {
      console.error("Order error:", e);
      toast.error(e.response?.data?.detail || "Erreur lors de la commande");
    }
    setLoading(false);
  };

  // Handle return from SumUp payment
  const urlParams = new URLSearchParams(window.location.search);
  const returnStatus = urlParams.get("status");
  const returnOrder = urlParams.get("order");

  if (returnStatus === "complete" && returnOrder) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="order-complete">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 rounded-full bg-[#8C9D78] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Paiement en cours de traitement
          </h1>
          <p className="text-stone-600 mb-2">
            Merci pour votre commande. Vous recevrez une confirmation par email.
          </p>
          <p className="text-sm text-stone-500 mb-8">
            Commande : <span className="font-mono">{returnOrder.slice(0, 8)}</span>
          </p>
          <div className="space-y-4">
            <Button asChild className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
              <Link to="/profile">Voir mes commandes</Link>
            </Button>
            <Button asChild variant="outline" className="w-full rounded-full">
              <Link to="/boutique">Continuer mes achats</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center" data-testid="order-complete">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 rounded-full bg-[#8C9D78] flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1D3557] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Commande confirmée !
          </h1>
          <p className="text-stone-600 mb-2">Merci pour votre commande.</p>
          <p className="text-sm text-stone-500 mb-8">
            Commande : <span className="font-mono">{orderId?.slice(0, 8)}</span>
          </p>
          <div className="space-y-4">
            <Button asChild className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
              <Link to="/profile">Voir mes commandes</Link>
            </Button>
            <Button asChild variant="outline" className="w-full rounded-full">
              <Link to="/boutique">Continuer mes achats</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!cart.items?.length) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1D3557] mb-4">Votre panier est vide</h1>
          <Button asChild className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full">
            <Link to="/boutique">Voir la boutique</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="checkout-page">
      <div className="container-custom py-12">
        <Link to="/panier" className="flex items-center gap-2 text-stone-500 hover:text-[#C65D47] mb-8">
          <ArrowLeft className="w-5 h-5" />
          Retour au panier
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold text-[#1D3557] mb-12" style={{ fontFamily: 'Fraunces, serif' }}>
          Finaliser la commande
        </h1>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
              {/* Shipping */}
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#C65D47] flex items-center justify-center">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                    Adresse de livraison
                  </h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="shipping_address">Adresse *</Label>
                    <Input id="shipping_address" required value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} placeholder="123 rue de la République" className="mt-2" data-testid="shipping-address-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="shipping_city">Ville *</Label>
                      <Input id="shipping_city" required value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} placeholder="Ajaccio" className="mt-2" data-testid="shipping-city-input" />
                    </div>
                    <div>
                      <Label htmlFor="shipping_postal">Code postal *</Label>
                      <Input id="shipping_postal" required value={form.shipping_postal} onChange={(e) => setForm({ ...form, shipping_postal: e.target.value })} placeholder="20000" className="mt-2" data-testid="shipping-postal-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="shipping_country">Pays</Label>
                    <Input id="shipping_country" value={form.shipping_country} onChange={(e) => setForm({ ...form, shipping_country: e.target.value })} className="mt-2" data-testid="shipping-country-input" />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optionnel)</Label>
                    <Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Instructions spéciales..." className="mt-2 resize-none" rows={3} data-testid="notes-input" />
                  </div>
                </div>
              </div>

              {/* Payment Mode */}
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#1D3557] flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1D3557]" style={{ fontFamily: 'Fraunces, serif' }}>
                    Mode de paiement
                  </h2>
                </div>

                <div className="grid gap-4">
                  {/* Option: 50% deposit */}
                  <label
                    className={`flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${paymentMode === "deposit" ? "border-[#8C9D78] bg-[#8C9D78]/5" : "border-stone-200 hover:border-stone-300"}`}
                    data-testid="payment-deposit"
                  >
                    <input type="radio" name="payment" value="deposit" checked={paymentMode === "deposit"} onChange={() => setPaymentMode("deposit")} className="mt-1 accent-[#8C9D78]" />
                    <div className="flex-1">
                      <p className="font-semibold text-[#1D3557]">Acompte 50% + solde à la livraison</p>
                      <p className="text-sm text-stone-500 mt-1">Payez {formatPrice(totalTTC * 0.5)} maintenant, le reste ({formatPrice(totalTTC * 0.5)}) à la réception.</p>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <div className="bg-[#8C9D78]/10 rounded-lg p-3 text-center">
                          <p className="text-xs text-stone-500">A payer maintenant</p>
                          <p className="text-lg font-bold text-[#8C9D78]">{formatPrice(totalTTC * 0.5)}</p>
                        </div>
                        <div className="bg-stone-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-stone-500">Solde à la livraison</p>
                          <p className="text-lg font-bold text-[#1D3557]">{formatPrice(totalTTC * 0.5)}</p>
                        </div>
                      </div>
                    </div>
                  </label>

                  {/* Option: Full payment */}
                  <label
                    className={`flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${paymentMode === "full" ? "border-[#8C9D78] bg-[#8C9D78]/5" : "border-stone-200 hover:border-stone-300"}`}
                    data-testid="payment-full"
                  >
                    <input type="radio" name="payment" value="full" checked={paymentMode === "full"} onChange={() => setPaymentMode("full")} className="mt-1 accent-[#8C9D78]" />
                    <div>
                      <p className="font-semibold text-[#1D3557]">Paiement intégral</p>
                      <p className="text-sm text-stone-500 mt-1">Payez {formatPrice(totalTTC)} en une seule fois.</p>
                    </div>
                  </label>
                </div>

                <div className="mt-4 flex items-center gap-2 text-xs text-stone-400">
                  <CreditCard className="w-3.5 h-3.5" />
                  Paiement sécurisé par SumUp · Carte bancaire
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-[#C65D47] hover:bg-[#A84A38] rounded-full h-14 text-lg font-semibold" data-testid="place-order-btn">
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Redirection vers le paiement...</>
                ) : (
                  `Payer ${formatPrice(payableNow)} via SumUp`
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-28">
              <h2 className="text-xl font-bold text-[#1D3557] mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
                Votre commande
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto mb-6">
                {cart.items.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                      <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-[#1D3557] text-sm truncate">{item.product_name}</h4>
                      <p className="text-sm text-stone-500">Qté: {item.quantity}</p>
                      <p className="text-sm font-semibold text-[#C65D47]">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-200 pt-4 space-y-3">
                <div className="flex justify-between text-stone-600">
                  <span>Sous-total HT</span>
                  <span>{formatPrice(totalHT)}</span>
                </div>
                <div className="flex justify-between text-stone-600">
                  <span>TVA (20%)</span>
                  <span>{formatPrice(tva)}</span>
                </div>
                <div className="flex justify-between text-stone-600">
                  <span>Livraison</span>
                  <span>Sur devis</span>
                </div>
                {isDeposit && (
                  <div className="flex justify-between text-[#8C9D78] font-medium">
                    <span>Acompte (50%)</span>
                    <span>{formatPrice(totalTTC * 0.5)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-stone-200 mt-4">
                <span className="text-lg font-semibold text-[#1D3557]">
                  {isDeposit ? "A payer maintenant" : "Total TTC"}
                </span>
                <span className="text-2xl font-bold text-[#C65D47]" data-testid="checkout-total">
                  {formatPrice(payableNow)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
